import React from 'react';
import { compact } from 'lodash';
import { Event, getEventSalesStatus } from '../../../../../model/event';
import { useAsync } from '../../../../../model/data';
import { loadPricingCategories, applyTax } from '../../../../../model/price';
import { Option } from '../../../../../components/Form/Select/Select';
import { t } from '../../../../../locales/translate';
import { loadContingent } from '../../../../../model/contingent';
import { printGeneralAdmissionTickets } from '../../../../../model/ticket';
import { pricingCategoryTotal } from '../Prices/PricingMatrix';
import { Stack } from '../../../../../components/Stack/Stack';
import { Tooltip } from '../../../../../components/Tooltip/Tooltip';
import { FormMultiSelect } from '../../../../../components/Form/Select/FormMultiSelect';
import { handleError } from '../../../../../model/notification';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../../../components/Form/FormForm';
import { FormSelect } from '../../../../../components/Form/Select/FormSelect';
import { FormNumberField } from '../../../../../components/Form/NumberField/FormNumberField';
import { FormTextField } from '../../../../../components/Form/TextField/FormTextField';
import { smoothScrollToElement } from '../../../../../util/ui-helpers';
import { formatCurrency } from '../../../../../util/formatters';
import { ToggleField } from '../../../../../components/Form/toggle_field/toggle_field';
import { EmailTemplateField } from '../../../../../components/Form/email_template_field/email_template_field';
import { AnimatedHeight } from '../../../../../components/AnimatedHeight/AnimatedHeight';
import { FormTextArea } from '../../../../../components/Form/TextArea/FormTextArea';

interface Props {
  event: Event;
  section: { id: string; pricingCategory: { id: string } | null };
  onTicketsCreated: (info: { sectionId: string }) => Promise<void>;
  onClose: () => void;
}

interface Params {
  productVariantId: string | null;
  comment: string;
  pool?: string;
  amount: number;
  tags?: string[];
  email: string;
  name: string;
  lastName?: string;
  phone?: string;
  sendViaEmail?: boolean;
  personalIdentifier?: string;
}

export function PrintSectionTicketOverlay({ section, event, onClose, onTicketsCreated }: Props): JSX.Element {
  const [loading] = useAsync(
    async () => Promise.all([loadPricingCategories({ event, local: true }), loadContingent(event, section.id)]),
    [section.id]
  );
  const { model, fields, valid, dirty, onSubmit, submissionStatus } = useForm<Partial<Params>>({
    model: {
      productVariantId: null,
      amount: 1,
    },
    validations: {
      amount: 'required',
      productVariantId: 'required',
      comment: 'required',
      email: [
        ({ model }) => {
          if (model.sendViaEmail && !model.email) {
            return ['required'];
          }
          return [];
        },
        'email',
      ],
      name: ({ model }) => {
        if (model.sendViaEmail && !model.name) {
          return ['required'];
        }
        return [];
      },
    },
    onSubmit: async () => {
      if (model.productVariantId && model.comment) {
        const amount = parseInt(model.amount as unknown as string);
        const tickets = {
          sectionId: section.id,
          productVariantId: model.productVariantId,
          pool: model.pool,
          amount,
          comment: model.comment,
          email: model.email,
          tags: model.tags,
          name: model.name,
          lastName: model.lastName,
          personalIdentifier: model.personalIdentifier,
        };

        await printGeneralAdmissionTickets(tickets);
        await loadContingent(event, section.id);
        await onTicketsCreated({ ...tickets });
        getEventSalesStatus(event.id, { force: true });
        smoothScrollToElement('print-history');
        onClose();
      }
    },
    onSubmitError: (error) => handleError(error),
  });

  const contingent = event.contingents?.find((e) => e.sectionId === section.id);
  const pools: Option[] = contingent?.pools?.map((e) => ({ value: e.name, label: `${e.name} (${e.count})` })) ?? [];
  const selectedPoolSize = contingent?.pools?.find((e) => e.name === model.pool)?.count ?? contingent?.available;
  const pricingCategoryName = event?.pricingCategories?.find((c) => c.id === section.pricingCategory?.id)?.name;
  const tags = Array.from(new Set(compact(event.printedTickets?.flatMap((a) => a.order?.tags)).sort())) || [];
  const variants = event.pricingCategories?.find((e) => e.id === section.pricingCategory?.id)?.productVariants ?? [];
  const selectedVariant = variants.find((e) => model.productVariantId === e.id);

  if (!selectedVariant && variants.length > 0) {
    fields.productVariantId.onChange(variants[0].id);
  }

  const canSubmit = [dirty, valid, contingent !== undefined].every(Boolean);

  return (
    <FormForm
      onSubmit={onSubmit}
      onClose={onClose}
      canSubmit={canSubmit}
      loading={loading || submissionStatus === 'submitting'}
      saveButtonTitle={!model.sendViaEmail ? t.actions.save('') : t.general.sendViaEmail}
    >
      <Stack>
        <Stack size="xs">
          {pricingCategoryName && (
            <Tooltip reference={<div>{pricingCategoryName}</div>}>{t.model.pricingCategory._name}</Tooltip>
          )}
          <FormSelect
            {...fields.pool}
            disabled={loading}
            options={pools}
            label={t.model.contingent.pool}
            allowEmpty
            allowEmptyMessage={`Available (${contingent?.available})`}
            displayWithoutOptions
            required
          />
          <FormNumberField {...fields.amount} label={t.general.amount} min={1} max={selectedPoolSize} />
          <FormSelect
            {...fields.productVariantId}
            disabled={loading}
            options={variants.map((e) => ({
              value: e.id,
              label: `${e.description} (${formatCurrency(applyTax(pricingCategoryTotal(e), e.taxRate))})`,
            }))}
            label={t.model.price.priceVariant}
            allowEmpty={variants.length === 0}
          />
          <FormMultiSelect
            {...fields.tags}
            label={t.model.ticket.logoTags}
            options={tags.map((tag) => ({
              id: tag,
              label: tag,
            }))}
            value={model.tags}
            input
            description={t.page.event.ticket.print_section_ticket_overlay.tags_description}
          />
          <FormTextField
            {...fields.personalIdentifier}
            label={t.model.ticket.personalIdentifier}
            description={t.model.ticket.personalIdentifierDescription}
          />
        </Stack>
        <ToggleField
          label={t.page.event.ticket.print_section_ticket_overlay.send_via_email}
          value={!!fields.sendViaEmail.value}
          onChange={(v) => fields.sendViaEmail.onChange(v)}
        />
        <AnimatedHeight>
          {!model.sendViaEmail ? (
            <FormTextArea {...fields.comment} label={t.model.comment._name} />
          ) : (
            <Stack>
              <Stack size="xs">
                <FormTextField {...fields.name} label={t.model.user.name} required />
                <FormTextField {...fields.lastName} label={t.model.user.lastName} />
                <FormTextField {...fields.email} type="email" label={t.model.user.email} required />
                <FormTextField {...fields.phone} type="number" label={t.model.user.phone} />
                <FormTextArea {...fields.comment} label={t.page.event.ticket.print_section_ticket_overlay.email_text} />
              </Stack>
              <EmailTemplateField
                eventId={event.id}
                data={{
                  firstName: model.name || null,
                  lastName: model.lastName || null,
                  email: model.email || null,
                  phone: model.phone || null,
                  ticketsCount: model.amount || null,
                  text: model.comment || null,
                }}
              />
            </Stack>
          )}
        </AnimatedHeight>
      </Stack>
    </FormForm>
  );
}
